import { useState, useEffect, useMemo, useRef } from "react";

import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import type { ColumnProps } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Skeleton } from "primereact/skeleton";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { LESSONS_CONTENT_TYPE_OPTIONS } from "sj-ui-toolkit/src/constants/content";
import type { CatalogTagOption } from "sj-ui-toolkit/src/client/svcContent";

import { loadLessonsAsync, loadSvcContentTagsAsync } from "@/api/resources";
import { useCweTags } from "@/hooks/useCweTags";
import LessonFilterPanel, { type FilterState } from "./LessonFilterPanel";

import { useFlags } from "launchdarkly-react-client-sdk";

const PATH_ONLY = "path_only";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const renderBooleanIcon = (value: boolean) => (
  <i
    aria-hidden="true"
    className={`pi ${value ? "pi-check-circle text-green-500" : "pi-times-circle text-red-500"}`}
  />
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SelectedLesson {
  id: string | number;
  name: string;
  description?: string;
  duration?: number;
  required?: boolean;
  status?: string;
  lessonKey?: string;
  lessonTypeFilter?: string;
}

export interface ContentValues {
  selectedLessons?: SelectedLesson[];
  orderedLessons?: SelectedLesson[];
  lessonGroupIds?: (string | number)[];
  requiredLessonIds?: (string | number)[];
  totalPoints?: number;
  [key: string]: unknown;
}

interface ValidationError {
  hasError: boolean;
  message: string;
}

interface PathContentStepProps {
  values: ContentValues;
  handleChange: (newValues: ContentValues) => void;
  showValidationMessages: boolean;
  validationErrors: ValidationError;
  selectedLessonsToAdd: SelectedLesson[];
  setSelectedLessonsToAdd: React.Dispatch<React.SetStateAction<SelectedLesson[]>>;
  selectedLessonsToRemove: SelectedLesson[];
  setSelectedLessonsToRemove: React.Dispatch<React.SetStateAction<SelectedLesson[]>>;
  preselectedLessonKeys?: string[];
}

interface AvailableLesson {
  id: string | number;
  name: string;
  description?: string;
  duration?: number;
  lessonTypeFilter?: string;
  lessonKey?: string;
  status?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PathContentStep = ({
  values,
  handleChange,
  showValidationMessages,
  validationErrors,
  selectedLessonsToAdd,
  setSelectedLessonsToAdd,
  selectedLessonsToRemove,
  setSelectedLessonsToRemove,
  preselectedLessonKeys,
}: PathContentStepProps) => {
  const preselectionAppliedRef = useRef(false);
  const [availableLessons, setAvailableLessons] = useState<AvailableLesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    focus: [],
    difficulty: [],
    release: [],
    contentType: [],
    languages: [],
    technologies: [],
    cwe: [],
  });
  const [tagsData, setTagsData] = useState<Record<string, CatalogTagOption[]>>({});
  const cweTags = useCweTags(tagsData);
  const { dynamicCatalogTags } = useFlags();

  const selectedLessons = values.selectedLessons ?? [];

  useEffect(() => {
    if (dynamicCatalogTags === undefined) return;
    const fetchTags = async () => {
      try {
        const tags = await loadSvcContentTagsAsync(!!dynamicCatalogTags);
        setTagsData(tags);
      } catch (error) {
        console.error("Failed to load tags:", error);
      }
    };
    void fetchTags();
  }, [dynamicCatalogTags]);

  useEffect(() => {
    const hasActiveFilters = Object.values(filters).some(arr => arr.length > 0);
    let isLatestRequest = true;

    const fetchLessons = async () => {
      setLoading(true);
      try {
        const apiFilters = {
          tags: [
            ...(filters.languages ?? []),
            ...(filters.technologies ?? []),
            ...(filters.focus ?? []),
          ],
          cwe_tags: (filters.cwe ?? []).map(cweId => `CWE-${cweId}`),
          difficulty: filters.difficulty ?? [],
          lesson_type: filters.contentType ?? [],
          release: filters.release ?? [],
          constraint: PATH_ONLY,
          hide_hands_on: false,
        };

        const lessons = hasActiveFilters
          ? await loadLessonsAsync(apiFilters)
          : await loadLessonsAsync({ constraint: PATH_ONLY, hide_hands_on: false } as Parameters<
              typeof loadLessonsAsync
            >[0]);
        if (isLatestRequest) setAvailableLessons(lessons ?? []);
      } catch (error) {
        console.error("Failed to load lessons:", error);
      } finally {
        if (isLatestRequest) setLoading(false);
      }
    };

    void fetchLessons();
    return () => {
      isLatestRequest = false;
    };
  }, [filters]);

  useEffect(() => {
    if (
      preselectionAppliedRef.current ||
      !preselectedLessonKeys?.length ||
      loading ||
      availableLessons.length === 0
    )
      return;
    preselectionAppliedRef.current = true;
    const currentSelectedIds = new Set((values.selectedLessons ?? []).map(l => l.id));
    const toAdd: SelectedLesson[] = availableLessons
      .filter(
        l =>
          l.lessonKey &&
          preselectedLessonKeys.includes(l.lessonKey) &&
          !currentSelectedIds.has(l.id)
      )
      .map(l => ({ ...l, required: true }));
    if (toAdd.length === 0) return;
    const newSelectedLessons = [...(values.selectedLessons ?? []), ...toAdd];
    handleChange({
      ...values,
      selectedLessons: newSelectedLessons,
      orderedLessons: newSelectedLessons,
      lessonGroupIds: newSelectedLessons.map(l => l.id),
      requiredLessonIds: newSelectedLessons.filter(l => l.required).map(l => l.id),
    });
    setActiveIndex(1);
  }, [loading, availableLessons]);

  const filteredAvailableLessons = useMemo(() => {
    const selectedIds = selectedLessons.map(l => l.id);
    return availableLessons.filter(l => !selectedIds.includes(l.id));
  }, [availableLessons, selectedLessons]);

  const handleAddLessons = () => {
    const newSelectedLessons: SelectedLesson[] = [
      ...selectedLessons,
      ...selectedLessonsToAdd.map(l => ({ ...l, required: true })),
    ];
    const newLessonGroupIds = newSelectedLessons.map(l => l.id);
    const newRequiredLessonIds = newSelectedLessons.filter(l => l.required).map(l => l.id);
    handleChange({
      ...values,
      selectedLessons: newSelectedLessons,
      orderedLessons: newSelectedLessons,
      lessonGroupIds: newLessonGroupIds,
      requiredLessonIds: newRequiredLessonIds,
    });
    setSelectedLessonsToAdd([]);
  };

  const handleRemoveLessons = () => {
    const newSelectedLessons = selectedLessons.filter(
      l => !selectedLessonsToRemove.some(s => s.id === l.id)
    );
    const newLessonGroupIds = newSelectedLessons.map(l => l.id);
    const newRequiredLessonIds = newSelectedLessons.filter(l => l.required).map(l => l.id);
    handleChange({
      ...values,
      selectedLessons: newSelectedLessons,
      orderedLessons: newSelectedLessons,
      lessonGroupIds: newLessonGroupIds,
      requiredLessonIds: newRequiredLessonIds,
    });
    setSelectedLessonsToRemove([]);
  };

  const handleToggleRequired = () => {
    const updatedLessons = selectedLessons.map(l => {
      const isSelected = selectedLessonsToRemove.some(s => s.id === l.id);
      return isSelected ? { ...l, required: !l.required } : l;
    });
    const newRequiredLessonIds = updatedLessons.filter(l => l.required).map(l => l.id);
    handleChange({
      ...values,
      selectedLessons: updatedLessons,
      orderedLessons: updatedLessons,
      requiredLessonIds: newRequiredLessonIds,
    });
    setSelectedLessonsToRemove([]);
  };

  const handleTabChange = (e: { index: number }) => {
    setActiveIndex(e.index);
    setSelectedLessonsToAdd([]);
    setSelectedLessonsToRemove([]);
  };

  const handleFilterChange = (category: keyof FilterState, vals: (string | number)[]) => {
    setFilters(prev => ({ ...prev, [category]: vals }));
  };

  const handleClearFilters = () => {
    setFilters({
      focus: [],
      difficulty: [],
      release: [],
      contentType: [],
      languages: [],
      technologies: [],
      cwe: [],
    });
  };

  const durationTemplate = (rowData: AvailableLesson | SelectedLesson) =>
    `${rowData.duration ?? 0} min`;

  const indexTemplate = (_rowData: unknown, options: { rowIndex: number }) => options.rowIndex + 1;

  const getLessonTypeLabel = (value: string) =>
    (LESSONS_CONTENT_TYPE_OPTIONS as { value: string; label: string }[]).find(
      o => o.value === value
    )?.label ?? value;

  const lessonTypeTemplate = (rowData: AvailableLesson) =>
    getLessonTypeLabel(rowData.lessonTypeFilter ?? "");

  const lessonTypeSortFunction = ({ data, order }: { data: AvailableLesson[]; order: number }) => {
    if (!data.length) return [];
    return [...data].sort((a, b) => {
      const result = getLessonTypeLabel(a.lessonTypeFilter ?? "").localeCompare(
        getLessonTypeLabel(b.lessonTypeFilter ?? "")
      );
      return order * result;
    });
  };

  const onRowReorder = (e: { value: SelectedLesson[] }) => {
    const reordered = e.value
      .map(r => selectedLessons.find(o => o.id === r.id))
      .filter((l): l is SelectedLesson => l !== undefined);
    handleChange({
      ...values,
      selectedLessons: reordered,
      orderedLessons: reordered,
      lessonGroupIds: reordered.map(l => l.id),
      requiredLessonIds: reordered.filter(l => l.required).map(l => l.id),
    });
  };

  const hasRequired = selectedLessonsToRemove.some(l => l.required);
  const hasOptional = selectedLessonsToRemove.some(l => !l.required);
  let toggleButtonLabel: string;
  if (hasRequired && !hasOptional) {
    toggleButtonLabel = `Make Optional (${selectedLessonsToRemove.length})`;
  } else if (!hasRequired && hasOptional) {
    toggleButtonLabel = `Make Required (${selectedLessonsToRemove.length})`;
  } else {
    toggleButtonLabel = `Toggle Optional (${selectedLessonsToRemove.length})`;
  }

  const renderHeader = () => (
    <div className="flex justify-between items-center">
      <div className="flex-1 max-w-md">
        {activeIndex === 0 && (
          <IconField iconPosition="right">
            <InputIcon className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={e => setGlobalFilterValue(e.target.value)}
              placeholder="Search lessons..."
              className="w-full"
              aria-label="Search available lessons"
            />
          </IconField>
        )}
      </div>
      <div className="flex gap-2">
        {activeIndex === 1 && (
          <Button
            label={toggleButtonLabel}
            onClick={handleToggleRequired}
            disabled={selectedLessonsToRemove.length === 0}
            severity="secondary"
          />
        )}
        <Button
          label={
            activeIndex === 0
              ? `Add ${selectedLessonsToAdd.length} Lessons`
              : `Remove ${selectedLessonsToRemove.length} Lessons`
          }
          onClick={activeIndex === 0 ? handleAddLessons : handleRemoveLessons}
          disabled={
            activeIndex === 0
              ? selectedLessonsToAdd.length === 0
              : selectedLessonsToRemove.length === 0
          }
        />
      </div>
    </div>
  );

  return (
    <>
      <p className="mb-6 text-secondary">
        Select lessons from the available catalog to include in this path. You can filter and search
        to find the right lessons.
      </p>

      <TabView aria-label="Path lessons" activeIndex={activeIndex} onTabChange={handleTabChange}>
        <TabPanel header="Available Lessons">
          <LessonFilterPanel
            isOpen={isFilterPanelOpen}
            onToggle={() => setIsFilterPanelOpen(p => !p)}
            tagsData={tagsData}
            cweTags={cweTags}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
          <DataTable
            value={loading ? Array(10).fill(null) : filteredAvailableLessons}
            paginator
            aria-label="Path content"
            aria-busy={loading}
            data-testid="path-content-available-lessons-table"
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            selectionMode="multiple"
            selection={selectedLessonsToAdd}
            onSelectionChange={e => setSelectedLessonsToAdd(e.value as SelectedLesson[])}
            isDataSelectable={({ data }) => data !== null}
            globalFilter={globalFilterValue}
            globalFilterFields={["name", "description"]}
            emptyMessage={
              filters.contentType.length === 1 && filters.contentType.includes("app_lab")
                ? "Not available for your organization. If you would like to enable these lessons, please reach out to your Account Manager."
                : "No lessons available"
            }
            header={renderHeader}
            size="large"
            tableClassName="dt-responsive-text"
          >
            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
            <Column
              field="name"
              header="Lesson Name"
              body={loading ? <Skeleton /> : undefined}
              sortable
              className="font-medium"
            />
            <Column
              field="description"
              header="Description"
              body={loading ? <Skeleton /> : undefined}
              sortable={false}
            />
            <Column
              field="lessonTypeFilter"
              header="Lesson Type"
              headerStyle={{ whiteSpace: "nowrap" }}
              body={loading ? <Skeleton /> : lessonTypeTemplate}
              sortable={!loading}
              sortFunction={lessonTypeSortFunction as ColumnProps["sortFunction"]}
            />
            <Column
              field="duration"
              header="Duration"
              body={loading ? <Skeleton /> : durationTemplate}
              sortable
              className="text-center"
            />
          </DataTable>
        </TabPanel>

        <TabPanel header={`Selected Lessons (${selectedLessons.length})`}>
          <DataTable
            value={selectedLessons}
            paginator
            aria-label="Selected path lessons"
            data-testid="path-content-selected-lessons-table"
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            selectionMode="multiple"
            selection={selectedLessonsToRemove}
            onSelectionChange={e => setSelectedLessonsToRemove(e.value as SelectedLesson[])}
            emptyMessage="No lessons selected yet"
            header={renderHeader}
            size="large"
            reorderableRows
            onRowReorder={onRowReorder}
            tableClassName="dt-responsive-text"
          >
            <Column rowReorder style={{ width: "3rem" }} />
            <Column
              header="#"
              body={indexTemplate}
              style={{ width: "3rem" }}
              className="text-center font-medium"
            />
            <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
            <Column field="name" header="Lesson Name" sortable={false} className="font-medium" />
            <Column field="description" header="Description" sortable={false} />
            <Column
              field="required"
              header="Required"
              body={(rowData: SelectedLesson) => renderBooleanIcon(rowData.required ?? true)}
              sortable={false}
              className="text-center"
              style={{ width: "6rem" }}
            />
            <Column
              field="duration"
              header="Duration"
              body={durationTemplate}
              sortable={false}
              className="text-center"
            />
          </DataTable>
        </TabPanel>
      </TabView>

      {showValidationMessages && validationErrors.hasError && (
        <div className="mt-4">
          <ValidationMessage severity="error" text={validationErrors.message} />
        </div>
      )}
    </>
  );
};

export default PathContentStep;
